import { useEffect, useState } from "react";
import api from "@/lib/api";
import { firstEmail } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Copy, EnvelopeSimple, ShieldCheck, ClockClockwise, CheckCircle, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * Dialog für Eigentümerportal-Verwaltung (Status, Einladung erstellen, Link, Mail).
 *
 * Wird aus Contacts.jsx aufgerufen, wenn ein Owner-Kontakt seine Portal-Verwaltung öffnen soll.
 */
export default function OwnerPortalDialog({ contact, open, onOpenChange }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [emailOverride, setEmailOverride] = useState("");

  const load = async () => {
    if (!contact?.id) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/owners/${contact.id}/invite-status`);
      setStatus(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Status konnte nicht geladen werden");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) { setStatus(null); setEmailOverride(""); load(); }
    // eslint-disable-next-line
  }, [open, contact?.id]);

  const createInvite = async () => {
    setCreating(true);
    try {
      await api.post(`/owners/${contact.id}/invite`, emailOverride ? { email: emailOverride } : {});
      toast.success("Einladung erstellt");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Einladung fehlgeschlagen");
    } finally { setCreating(false); }
  };

  const sendMail = async (inviteId) => {
    try {
      await api.post(`/owners/invites/${inviteId}/send-mail`);
      toast.success("Einladung per E-Mail versendet");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Versand fehlgeschlagen");
    }
  };

  const revoke = async (inviteId) => {
    if (!await window._domivusConfirm("Einladung widerrufen?")) return;
    try {
      await api.delete(`/owners/invites/${inviteId}`);
      toast.success("Einladung widerrufen");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Fehler"); }
  };

  const setupLink = (token) => `${window.location.origin}/portal/setup/${token}`;

  const copyLink = async (token, inviteId) => {
    try {
      await navigator.clipboard.writeText(setupLink(token));
      await api.post(`/owners/invites/${inviteId}/mark-copied`);
      toast.success("Link kopiert");
      load();
    } catch {
      toast.error("Link konnte nicht kopiert werden");
    }
  };

  const invite = status?.latest_invite;
  const portalActive = status?.portal_active;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="owner-portal-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck size={22} className="text-[#0F2A4D]" />
            Eigentümerportal verwalten
          </DialogTitle>
          <DialogDescription>
            {contact?.salutation === "herr" ? "Herr " : contact?.salutation === "frau" ? "Frau " : ""}
            {contact?.first_name} {contact?.last_name}
            {contact?.company_name ? ` · ${contact.company_name}` : ""}
          </DialogDescription>
        </DialogHeader>

        {loading && <div className="text-sm text-slate-500 text-center py-8">Lade Status…</div>}

        {!loading && status && (
          <div className="space-y-4">
            {/* Status-Karte */}
            <div className={`border rounded-md p-4 ${portalActive ? "border-emerald-200 bg-emerald-50" : invite?.status === "pending" ? "border-amber-200 bg-amber-50" : "border-slate-200 bg-slate-50"}`} data-testid="portal-status">
              <div className="flex items-start gap-3">
                {portalActive ? <CheckCircle size={22} className="text-emerald-600" weight="fill" /> :
                  invite?.status === "pending" ? <ClockClockwise size={22} className="text-amber-600" /> :
                  <Warning size={22} className="text-slate-500" />}
                <div className="flex-1">
                  <div className="font-semibold text-slate-900">
                    {portalActive ? "Portal aktiv – Eigentümer kann sich anmelden"
                      : invite?.status === "pending" ? "Einladung versendet – wartet auf Aktivierung durch Eigentümer"
                      : invite?.status === "expired" ? "Einladung abgelaufen"
                      : invite?.status === "revoked" ? "Einladung widerrufen"
                      : "Noch keine Einladung erstellt"}
                  </div>
                  {invite && (
                    <div className="text-xs text-slate-600 mt-1 space-y-0.5">
                      <div>Versandadresse: <span className="font-mono">{invite.email}</span></div>
                      {invite.sent_at && <div>Per Mail versendet: {new Date(invite.sent_at).toLocaleString("de-DE")}</div>}
                      {invite.last_link_copied_at && <div>Link kopiert: {new Date(invite.last_link_copied_at).toLocaleString("de-DE")}</div>}
                      {invite.expires_at && <div>Gültig bis: {new Date(invite.expires_at).toLocaleString("de-DE")}</div>}
                      {invite.accepted_at && <div className="text-emerald-700 font-semibold">Aktiviert: {new Date(invite.accepted_at).toLocaleString("de-DE")}</div>}
                    </div>
                  )}
                  {status.owner_user?.last_login_at && (
                    <div className="text-xs text-slate-600 mt-1">Letzte Anmeldung: {new Date(status.owner_user.last_login_at).toLocaleString("de-DE")}</div>
                  )}
                </div>
              </div>
            </div>

            {/* Aktiver Einladungslink */}
            {invite?.status === "pending" && (
              <div className="border border-slate-200 rounded-md p-4 bg-white space-y-3" data-testid="active-invite-actions">
                <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Aktiver Einladungslink</div>
                <div className="flex items-center gap-2">
                  <Input value={setupLink(invite.token)} readOnly className="font-mono text-xs h-9" onClick={(e) => e.target.select()} data-testid="invite-link-input" />
                  <Button variant="outline" size="sm" className="h-9 gap-1.5" onClick={() => copyLink(invite.token, invite.id)} data-testid="copy-invite-link">
                    <Copy size={14} /> Link kopieren
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2 pt-1">
                  <Button size="sm" className="bg-[#0F2A4D] hover:bg-[#0F2A4D]/90 gap-1.5" onClick={() => sendMail(invite.id)} data-testid="send-invite-mail">
                    <EnvelopeSimple size={14} /> {invite.sent_at ? "Mail erneut senden" : "Per E-Mail versenden"}
                  </Button>
                  <Button size="sm" variant="ghost" className="text-rose-700 hover:bg-rose-50" onClick={() => revoke(invite.id)} data-testid="revoke-invite">
                    Widerrufen & neu erstellen
                  </Button>
                </div>
              </div>
            )}

            {/* Neue Einladung */}
            {(!invite || invite.status !== "pending") && !portalActive && (
              <div className="border border-slate-200 rounded-md p-4 bg-white space-y-3" data-testid="new-invite-section">
                <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Eigentümerportal-Einladung erstellen</div>
                <div className="text-xs text-slate-600">
                  Es wird ein sicherer Setup-Link erzeugt (14 Tage gültig). Der Eigentümer setzt sein Passwort selbst.
                  Sie entscheiden, ob die Einladung per E-Mail versendet oder der Link manuell weitergegeben wird.
                </div>
                <div>
                  <Label className="text-xs">E-Mail (optional, sonst aus Kontakt)</Label>
                  <Input type="email" value={emailOverride} onChange={(e) => setEmailOverride(e.target.value)}
                    placeholder={firstEmail(contact?.emails) || "..."} data-testid="invite-email-override" className="h-9" />
                </div>
                <Button onClick={createInvite} disabled={creating} className="bg-[#0F2A4D] hover:bg-[#0F2A4D]/90 gap-2" data-testid="create-invite-btn">
                  <ShieldCheck size={16} /> {creating ? "Erstelle…" : "Einladung erstellen"}
                </Button>
              </div>
            )}

            {portalActive && (
              <div className="text-xs text-slate-500 italic">
                Tipp: Sie können oben „Widerrufen & neu erstellen" wählen, falls der Eigentümer einen neuen Link benötigt.
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Schließen</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
